import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { backendService } from '../services/backendService';

interface AppContextType {
  electron: string | null;
  setElectron: (value: string | null) => void;
  deviceId: string;
  internal: string;
  isElectronMode: boolean;
  photoProcessingMode: 'raster' | 'style-only' | null;
  setPhotoProcessingMode: (value: 'raster' | 'style-only' | null) => void;
  config: any;
  isConfigLoading: boolean;
  configError: string | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [electron, setElectron] = useState<string | null>(null);
  const [internal, setInternal] = useState<string>('0');
  // 立即初始化 deviceId，避免使用默认值导致多次加载
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deviceId, _setDeviceId] = useState<string>(() => {
    // 在初始化时就读取 URL 参数和 localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const deviceParam = urlParams.get('device');
    
    try {
      if (deviceParam !== null && deviceParam.trim() !== '') {
        localStorage.setItem('deviceId', deviceParam);
        return deviceParam;
      }
      const storedDeviceId = localStorage.getItem('deviceId');
      if (storedDeviceId && storedDeviceId.trim() !== '') {
        return storedDeviceId;
      }
      const newDeviceId = 'device_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
      localStorage.setItem('deviceId', newDeviceId);
      return newDeviceId;
    } catch (error) {
      console.warn('localStorage不可用，使用临时deviceId:', error);
      return "global";
    }
  });
  const [photoProcessingMode, setPhotoProcessingMode] = useState<'raster' | 'style-only' | null>(null);

  // 配置相关状态
  const [config, setConfig] = useState<any>(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  // 计算布尔值，方便其他组件使用
  const isElectronMode = electron === '1' || electron === 'true';

  // 在组件挂载时读取URL查询参数
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    const electronParam = urlParams.get('electron');
    const internalParam = urlParams.get('internal');
    
    if (electronParam !== null) {
      setElectron(electronParam);
    }
    
    if (internalParam !== null) {
      setInternal(internalParam);
    }
    
    console.log('AppContext初始化完成，deviceId:', deviceId, 'internal:', internalParam || '0');
  }, [deviceId]);

  // 加载配置信息
  useEffect(() => {
    // 跳过初始渲染时的 'global' 值（如果有的话）
    if (!deviceId || deviceId === 'global') {
      console.log('跳过配置加载，deviceId无效:', deviceId);
      return;
    }

    const loadConfig = async () => {
      setIsConfigLoading(true);
      setConfigError(null);
      
      try {
        // 直接使用deviceId参数和internal参数
        const configData = await backendService.getConfig(deviceId, internal);
        setConfig(configData);
        console.log('配置加载成功 (deviceId: ' + deviceId + ', internal: ' + internal + '):', configData);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : '未知错误';
        setConfigError(errorMessage);
        console.error('配置加载失败:', error);
      } finally {
        setIsConfigLoading(false);
      }
    };

    // 始终加载配置（移除aiProvider条件判断）
    loadConfig();
  }, [deviceId, internal]);

  return (
    <AppContext.Provider value={{
      electron,
      setElectron,
      deviceId,
      internal,
      isElectronMode,
      photoProcessingMode,
      setPhotoProcessingMode,
      config,
      isConfigLoading,
      configError
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
